import { useMutation } from '@tanstack/react-query';

import { getApiErrorMessage } from '@/lib/api/client';
import { postData, postFormData } from '@/lib/api/client-v2';
import { DATE_FR, DATE_ISO, formatDate, isValidDate, type DateInput } from '@/lib/dates';
import { notify, type NotifyOptions } from '@/lib/notify';
import { useUiStore } from '@/stores/ui-store';
import { ENDPOINTS } from '@/types/api/endpoints';

/*
 * Avenants : reprise de l’ancien middleware `amendment` (changement d'adresse, de coordonnées,
 * d'IBAN, de fractionnement, Ma période hivernale).
 *
 * Chaque mutation reçoit le contrat visé (`contractId`), les valeurs du formulaire (`form`) et,
 * le cas échéant, la fonction appelée à la confirmation de la popin de succès
 * (`successFunction`).
 */

export interface AmendmentVariables<TForm = AmendmentForm> {
    contractId: string | number;
    form: TForm;
    /** Appelée à la confirmation de la popin de succès. */
    successFunction?: () => void;
}

/** Valeurs d'un formulaire d'avenant : transmises telles quelles, hors transformations documentées. */
export type AmendmentForm = Record<string, unknown>;

/** Ajoute une valeur au `FormData` comme le faisait l'ancien code (conversion en chaîne implicite). */
const appendValue = (data: FormData, key: string, value: unknown) => {
    data.append(key, value instanceof Blob ? value : String(value));
};

const errorPopin = (error: unknown) =>
    notify({
        html: '<p>Une erreur est survenue : ' + getApiErrorMessage(error) + '</p>',
        icon: 'error',
        title: 'Attention !',
        confirmButtonText: "J'ai compris",
    });

/* ---------- Changement d'adresse ---------- */

export interface ChangeAddressForm {
    sansStationnement?: boolean;
    new_adress_is_new_lieuStationnement?: boolean;
    adresseNew?: string | null;
    adresse2New?: string | null;
    adresse3New?: string | null;
    villeNew?: string;
    codePostalNew?: string;
    ville_new_lieuStationnement?: string;
    cp_new_lieuStationnement?: string;
}

/** Champ facultatif : une saisie vide n'est pas envoyée (le Pass refuse une chaîne vide). */
const optional = (value: string | null | undefined) => (value ? value : undefined);

/**
 * Corps v2 du déménagement. Le lieu de stationnement est la nouvelle adresse ou la saisie
 * dédiée ; le stationnement actuel et le code produit, le Pass les lit sur le contrat.
 */
export const toAddressBody = (form: ChangeAddressForm) => {
    const parking = form.sansStationnement
        ? {}
        : form.new_adress_is_new_lieuStationnement
          ? { stationnement_cp: form.codePostalNew, stationnement_ville: form.villeNew }
          : { stationnement_cp: form.cp_new_lieuStationnement, stationnement_ville: form.ville_new_lieuStationnement };

    return {
        adresse: form.adresseNew,
        adresse_complement: optional(form.adresse2New),
        adresse_batiment: optional(form.adresse3New),
        code_postal: form.codePostalNew,
        ville: form.villeNew,
        ...parking,
    };
};

/**
 * Déménagement (`POST /contracts/{id}/address`).
 *
 * Au succès, si le Pass renvoie un `avenant_url` (la zone change), la popin propose d'ouvrir le
 * formulaire d'avenant en mode « changement d'adresse » ; sinon elle appelle `successFunction`.
 */
export const useChangeAddress = () =>
    useMutation({
        mutationFn: ({ contractId, form }: AmendmentVariables<ChangeAddressForm>) =>
            postData<{ avenant_url?: string } | null>(ENDPOINTS.contractAddress(contractId), toAddressBody(form)),
        onSuccess: (data, { successFunction }) => {
            void notify({
                html: 'Vous avez déclaré un changement d’adresse. Afin de l’enregistrer et d’adapter votre contrat à votre nouvelle localisation, un avenant est nécessaire.',
                icon: 'success',
                /* `title: null` à l'origine : pas de titre. */
                confirmButtonText: 'Faire un avenant',
                successFunction: () => {
                    if (data?.avenant_url) {
                        /* Redirection vers le formulaire d'avenant en mode "changement d'adresse" uniquement (changement de zone) */
                        const separator = data.avenant_url.includes('?') ? '&' : '?';
                        window.open(`${data.avenant_url}${separator}motifAdresse=1`, '_blank');
                    } else if (successFunction) {
                        successFunction();
                    }
                },
            });
        },
        onError: (error) => {
            void errorPopin(error);
        },
    });

/* ---------- Changement de coordonnées ---------- */

export interface ChangeContactVariables<TForm> {
    /** Tiers de l'assuré (`belair_id`). */
    tiersId: string | number;
    form: TForm;
    /** Appelée à la confirmation de la popin de succès. */
    successFunction?: () => void;
}

/**
 * Coordonnées (email, téléphone) : `POST /tiers/{id}/contact`. Le corps porte le mot de passe de
 * l'assuré, que le Pass vérifie avant d'écrire.
 */
export const useChangeContact = <TForm = AmendmentForm>() =>
    useMutation({
        mutationFn: ({ tiersId, form }: ChangeContactVariables<TForm>) => postData<null>(ENDPOINTS.tiersContact(tiersId), form),
        onSuccess: (_data, { successFunction }) => {
            void notify({
                html: '<p>Vous venez de modifier vos coordonnées. <br/> Pour finaliser cette mise à jour, veuillez consulter votre boîte de réception et suivre les instructions du mail de validation que nous venons de vous envoyer.</p>',
                type: 'success',
                title: 'Vos informations seront mises à jour sous 24h',
                confirmButtonText: "J'ai compris",
                successFunction,
            } as NotifyOptions);
        },
        onError: (error) => {
            void errorPopin(error);
        },
    });

/* ---------- Changement d'IBAN ---------- */

export interface ChangeIbanForm {
    /** Pièces indexées par code de document, envoyées en `files[code]`. */
    files?: Record<string, Blob | undefined>;
    /** `P` (personne physique) : la date de naissance est alors envoyée en `yyyy-MM-dd`. */
    type?: string;
    birthdate?: DateInput;
    iban?: string;
    titulaire?: string;
}

export interface ChangeIbanVariables {
    /**
     * Contrat de l'URL (`/contracts/{id}/rib`) : celui que le corps vise déjà (`contract`, tiers
     * payeur) ou, à défaut, le contrat de référence de l'écran. Le corps n'est pas modifié pour
     * autant : `contract` n'y figure que si l'écran l'a renseigné, comme en v1.
     */
    contractId: string | number;
    form: ChangeIbanForm;
    /**
     * Titulaire envoyé quand l'assuré ne le change pas : la v2 l'exige (hors `sans_prelevement`)
     * là où ABS, en v1, retombait sur la raison sociale du tiers (`B_RSOCIALE`, 60 caractères).
     */
    defaultTitulaire?: string | null;
    /** Changement de titulaire du compte : la popin de succès le mentionne. */
    changementTitulaire?: boolean;
    /** Appelée à la confirmation de la popin de succès. */
    successFunction?: () => void;
}

/**
 * Changement d'IBAN / de titulaire (`POST /contracts/{id}/rib`), en multipart.
 *
 * Les pièces partent en `files[code]` (seules les pièces jointes, pas les emplacements vides),
 * l'IBAN sans tirets, la date de naissance d'une personne physique au format `yyyy-MM-dd`.
 */
export const useChangeIban = () =>
    useMutation({
        mutationFn: ({ contractId, form, defaultTitulaire }: ChangeIbanVariables) => {
            const data = new FormData();
            const body: AmendmentForm = { ...form };

            if (!String(body.titulaire ?? '').trim() && defaultTitulaire) {
                /* 60 caractères au plus, comptés par point de code */
                body.titulaire = Array.from(defaultTitulaire.trim()).slice(0, 60).join('');
            }

            Object.keys(body).forEach((key) => {
                const value = body[key];

                if (key === 'files') {
                    const files = (value ?? {}) as Record<string, unknown>;
                    Object.keys(files).forEach((doc) => {
                        if (files[doc] instanceof Blob) {
                            data.append(`files[${doc}]`, files[doc]);
                        }
                    });
                } else if (key === 'birthdate' && form.type === 'P' && form.birthdate && isValidDate(form.birthdate)) {
                    data.append(key, formatDate(form.birthdate, DATE_ISO));
                } else if (key === 'iban') {
                    data.append(key, String(value).replace(/[-]/g, ''));
                } else {
                    appendValue(data, key, value);
                }
            });

            return postFormData<null>(ENDPOINTS.contractRib(contractId), data);
        },
        onSuccess: (_data, { changementTitulaire = false, successFunction }) => {
            let message = '';
            if (changementTitulaire) {
                message = "<p>Votre demande de changement d'adresse a bien été enregistré.</p>";
            }

            void notify({
                html: '<div>' + message + '<p>Vos informations seront mises à jour sous 24h</p></div>',
                type: 'success',
                title: 'Félicitations !',
                confirmButtonText: "J'ai compris",
                successFunction,
            } as NotifyOptions);
        },
        onError: (error) => {
            void errorPopin(error);
        },
    });

/* ---------- Passage en prélèvement ---------- */

export interface ChangeFractionnementForm {
    fractionnement?: string;
    iban?: string;
    /** RIB / IBAN importé, envoyé en `files[G]`. */
    files?: Blob;
}

export interface ChangeFractionnementVariables extends AmendmentVariables<ChangeFractionnementForm> {
    /** Hors période d'échéance : un chrono est créé, la popin l'annonce. */
    createChrono?: boolean | null;
}

/** Passage en prélèvement (`POST /contracts/{id}/fractionnement`), en multipart. */
export const useChangeFractionnement = () =>
    useMutation({
        mutationFn: ({ contractId, form }: ChangeFractionnementVariables) => {
            const data = new FormData();
            /* Copie de surface : seules les clés de premier niveau sont modifiées. */
            const clone: AmendmentForm = { ...form };

            if (clone.iban) {
                clone.iban = String(clone.iban).replace(/[-]/g, '');
            } else {
                delete clone.iban;
            }

            Object.keys(clone).forEach((key) => {
                if (key === 'files') {
                    appendValue(data, 'files[G]', clone[key]);
                } else {
                    appendValue(data, key, clone[key]);
                }
            });

            return postFormData<null>(ENDPOINTS.contractFractionnement(contractId), data);
        },
        onSuccess: (_data, { createChrono, successFunction }) => {
            let message =
                'Vos informations seront mises à jour sous 24h. <br/>Pour finaliser votre demande, veuillez signer électroniquement le mandat SEPA qui va vous être envoyé par email.';

            if (createChrono) {
                message = "Vos informations seront mises à jour sous 24h. <br/> Un email d'information vient de vous être envoyé.";
            }

            void notify({
                html: message,
                type: 'success',
                title: 'Félicitations !',
                confirmButtonText: "J'ai compris",
                successFunction,
            } as NotifyOptions);
        },
        onError: (error) => {
            void errorPopin(error);
        },
    });

/* ---------- Ma période hivernale ---------- */

export interface HivernageForm {
    dateFin?: DateInput;
}

export interface HivernageVariables {
    contractId: string | number;
    form: HivernageForm;
    /** Appelée à la confirmation de la popin de succès. */
    successFunction?: () => void;
}

/** Corps v2 de l'aperçu et de l'activation : `date_fin` au format `dd/MM/yyyy`. */
const hivernageBody = (form: HivernageForm) => ({ date_fin: formatDate(form.dateFin, DATE_FR) });

/** Activation de Ma période hivernale (`POST /contracts/{id}/hivernage/confirm`). */
export const useActivateHivernage = () =>
    useMutation({
        mutationFn: ({ contractId, form }: HivernageVariables) =>
            postData<null>(ENDPOINTS.contractHivernageConfirm(contractId), hivernageBody(form)),
        onSuccess: (_data, { successFunction }) => {
            void notify({
                html: 'Votre demande a été enregistrée.<br/>Pour la valider, veuillez signer électroniquement la lettre avenant qui va vous être envoyée par e-mail',
                type: 'success',
                title: 'Félicitations !',
                confirmButtonText: "J'ai compris",
                successFunction,
            } as NotifyOptions);
        },
        onError: () => {
            void notify({
                html: '<p>Une erreur est survenue.</p>',
                icon: 'error',
                title: 'Attention !',
                confirmButtonText: "J'ai compris",
            });
        },
    });

/**
 * Estimation de la réduction de Ma période hivernale (`POST /contracts/{id}/hivernage/preview`).
 * La réduction (`data.reduction`, chaîne `12,34`) est rangée dans `useUiStore().hivernageReduction`.
 * La date maximale (`data.max_date`) n'est pas lue : l'écran la calcule avant l'aperçu.
 */
export const useHivernageReduction = () =>
    useMutation({
        mutationFn: ({ contractId, form }: Omit<HivernageVariables, 'successFunction'>) =>
            postData<{ reduction?: string; max_date?: string } | null>(
                ENDPOINTS.contractHivernagePreview(contractId),
                hivernageBody(form),
            ),
        onSuccess: (data) => {
            useUiStore.getState().setHivernageReduction(data?.reduction);
        },
        onError: () => {
            void notify({
                html: '<p>Une erreur est survenue.</p>',
                icon: 'error',
                title: 'Attention !',
                confirmButtonText: "J'ai compris",
            });
        },
    });

/** Désactivation anticipée de Ma période hivernale (`POST /contracts/{id}/hivernage/end`), sans corps. */
export const useEndHivernage = () =>
    useMutation({
        mutationFn: ({ contractId }: { contractId: string | number }) =>
            postData<null>(ENDPOINTS.contractHivernageEnd(contractId)),
        onSuccess: () => {
            void notify({
                icon: 'success',
                body: 'Ma période hivernale est bien désactivée',
                confirmButtonText: "J'ai compris",
            } as NotifyOptions);
        },
        onError: (error) => {
            const message = getApiErrorMessage(error) || 'Une erreur est survenue : ';

            void notify({
                html: `<p>${message}</p>`,
                icon: 'error',
                confirmButtonText: "J'ai compris",
            });
        },
    });
